import { useCallback, useEffect, useState } from "react";
import { Badge, Button } from "react-bootstrap";
import { FaCloud, FaCloudUploadAlt, FaSignOutAlt } from "react-icons/fa";
import { Link, Outlet, useNavigate } from "react-router-dom";
import MimicBanner from "../components/app/MimicBanner.jsx";
import { logout } from "../services/apiClient.js";
import { countUploads } from "./storage/inspectionStore.js";
import { flushOutbox } from "./services/outbox.js";
import { processUploadQueue } from "./services/uploadQueue.js";
import "./inspection.css";

function InspectionLayout({ currentUser, setCurrentUser }) {
  const navigate = useNavigate();
  const [pending, setPending] = useState(0);
  const [online, setOnline] = useState(navigator.onLine);

  const refreshPending = useCallback(() => {
    countUploads(currentUser.id).then(setPending).catch(() => setPending(0));
  }, [currentUser.id]);

  // Photos first, THEN any queued finish: the finish reports how many photos are
  // still outstanding, so it must be sent after the drain has had its go — and
  // the office should see the photos arriving before the job says it is done.
  const resumeUploads = useCallback(() => {
    if (!navigator.onLine) return;
    processUploadQueue(currentUser.id)
      .then(() => flushOutbox(currentUser.id))
      .catch(() => {})
      .finally(refreshPending);
  }, [currentUser.id, refreshPending]);

  useEffect(() => {
    refreshPending();
    resumeUploads();
    function changed() {
      refreshPending();
    }
    function cameOnline() {
      setOnline(true);
      resumeUploads();
    }
    function wentOffline() {
      setOnline(false);
    }
    function visible() {
      if (document.visibilityState === "visible") resumeUploads();
    }
    window.addEventListener("inspection-queue-changed", changed);
    window.addEventListener("online", cameOnline);
    window.addEventListener("offline", wentOffline);
    document.addEventListener("visibilitychange", visible);
    return () => {
      window.removeEventListener("inspection-queue-changed", changed);
      window.removeEventListener("online", cameOnline);
      window.removeEventListener("offline", wentOffline);
      document.removeEventListener("visibilitychange", visible);
    };
  }, [refreshPending, resumeUploads]);

  async function handleLogout() {
    if (pending && !window.confirm(
      `${pending} photo${pending === 1 ? " is" : "s are"} still waiting to upload. ` +
      "They will remain safely on this device for this account. Log out anyway?"
    )) return;
    await logout();
    setCurrentUser(null);
    navigate("/login");
  }

  return (
    <div className="inspection-shell min-vh-100 bg-body-tertiary">
      <MimicBanner currentUser={currentUser} />
      <header className="inspection-header bg-body border-bottom shadow-sm">
        <div className="container-fluid d-flex align-items-center gap-3 py-2">
          <Link to="/inspection" className="d-flex align-items-center text-decoration-none">
            <img className="inspection-logo" src="/images/logo3-clean.png" alt="GPS" />
          </Link>
          <div className="flex-grow-1">
            <div className="fw-bold">My Inspections</div>
            <div className="small text-body-secondary">{currentUser.full_name}</div>
          </div>
          <Badge bg={online ? "success" : "secondary"} className="d-none d-sm-inline-flex align-items-center gap-1">
            <FaCloud /> {online ? "Online" : "Offline"}
          </Badge>
          {pending ? (
            <Badge bg="warning" text="dark" className="d-inline-flex align-items-center gap-1">
              <FaCloudUploadAlt /> {pending}
            </Badge>
          ) : null}
          <Button variant="outline-secondary" size="sm" onClick={handleLogout} aria-label="Logout">
            <FaSignOutAlt />
          </Button>
        </div>
      </header>
      <main className="container-fluid inspection-content py-3">
        <Outlet />
      </main>
    </div>
  );
}

export default InspectionLayout;
